/**
 * FiduciaryCipSection — CIP capture for the Fiduciary dialog.
 *
 * Rationale: per Bill's 2026-04-24 build-card heads-up — the FIDUCIARY is a
 * human acting on behalf of someone else, so they HAVE a photo ID. This
 * section mirrors `IndividualCipScanSection` structurally (IdCapturePanel +
 * manual CIP field grid) but:
 *
 *   - uses the fiduciary-sub-type-driven primary docCode via
 *     attachedDocs.getPrimaryIdDocCode() (REP_PAYEE_ID / CONSERVATOR_ID /
 *     FIDUCIARY_ID). The IdCapturePanel itself receives the person with its
 *     customerType already set by FiduciaryIdentitySection, so the mapping
 *     flows naturally through the shared attachedDocs.ts helper.
 *   - labels the ID as "Fiduciary's ID" to clarify scope — this is NOT the
 *     protected person's ID (card: DL not required for beneficiary/ward).
 *
 * TODO(bill): fiduciary walkthrough needed:
 *   - Confirm Field 254 (IdVerifyCode) defaults per sub-type. Card candidates:
 *     RepPayee/VAFiduciary → 8 (Other); Conservator → 6 (State Documents).
 *     Not auto-defaulted here until walkthrough lands.
 *   - Confirm "Appointment Instrument" separate doc slot vs. using the ID
 *     capture as the appointment-doc evidence (they're different artifacts —
 *     SSA letter != DL). Currently we capture ID here; appointment doc
 *     attaches via the DocumentSlotsSection grid below.
 */

import type { CSSProperties } from 'react';
import { CollapsibleSection } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import { IdCapturePanel } from '../../scan/IdCapturePanel';
import { ID_TYPE_CODES } from '../individual/individual-config';
import type { AttachedDoc } from '../shared/attachedDocs';
import { FIDUCIARY_HELPER_TEXT } from './fiduciary-config';

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------

const labelStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

function changedFieldStyle(base: CSSProperties, changed: boolean): CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function patchExtra(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

function isDateInPast(iso: string): boolean {
  if (!iso) return false;
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return iso < `${yyyy}-${mm}-${dd}`;
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface FiduciaryCipSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  onDocumentCaptured?: (doc: AttachedDoc) => void;
  readOnly?: boolean;
  /** When false, hides the IdCapturePanel. Defaults true (New Acct flow). */
  cipScanEnabled?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function FiduciaryCipSection({
  person,
  onChange,
  mode,
  isChanged,
  onDocumentCaptured,
  readOnly = false,
  cipScanEnabled = true,
}: FiduciaryCipSectionProps): JSX.Element {
  const ic = isChanged ?? ((_: string) => false);

  const idType       = readExtra(person, 'idType');
  const idNumber     = readExtra(person, 'idNumber');
  const idIssuer     = readExtra(person, 'idIssuer');
  const idIssueDate  = readExtra(person, 'idIssueDate');
  const idExpiryDate = readExtra(person, 'idExpiryDate');

  const isExpired = isDateInPast(idExpiryDate);
  const expandedInitial = mode === 'new' || isExpired;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {isExpired && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--err-bg)',
            border: '1px solid var(--pcb-red, #b10505)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Fiduciary ID expired — update required.</strong> The ID on file expired on{' '}
          <strong>{idExpiryDate}</strong>. Capture a current identification before committing the
          CIF modification.
        </div>
      )}

      <CollapsibleSection
        title="Fiduciary's Customer Identification (CIP)"
        iconColor="var(--pcb-red, #b10505)"
        defaultOpen={expandedInitial}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--ink-600)', fontStyle: 'italic' }}>
            {FIDUCIARY_HELPER_TEXT.cipIntro}
          </div>

          {/* Scan pipeline — IdCapturePanel uses attachedDocs.getPrimaryIdDocCode
              to route the captured image to the matching DocumentRequirements
              slot (REP_PAYEE_ID / CONSERVATOR_ID / FIDUCIARY_ID) based on the
              entity's customerType. */}
          {cipScanEnabled && !readOnly && (
            <IdCapturePanel
              person={person}
              onChange={onChange}
              mode={mode}
              defaultExpanded={expandedInitial}
              onDocumentCaptured={onDocumentCaptured}
            />
          )}

          {/* Manual entry grid — mirrors IndividualCipScanSection. Same extra
              keys (idType / idNumber / idIssuer / idIssueDate / idExpiryDate)
              so useInquiryHydration + useSaveDispatch need no special-casing. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle} htmlFor="rao-fid-idtype">
                ID Type (Field 254)
              </label>
              <select
                id="rao-fid-idtype"
                value={idType}
                onChange={(e) =>
                  onChange(patchExtra(person, { idType: e.currentTarget.value }))
                }
                disabled={readOnly}
                style={changedFieldStyle(inputStyle, ic('idType'))}
                title="Fiduciary ID Type"
              >
                {ID_TYPE_CODES.map((t) => (
                  <option key={t.value || 'none'} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {/* TODO(bill): fiduciary walkthrough needed — card suggests
                  sub-type-specific defaults:
                    RepPayee/VAFiduciary → 8 (Other)
                    Conservator → 6 (State Documents)
                  Not auto-seeded pending §15 confirmation. */}
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-fid-idnum">
                ID Number (Field 255)
              </label>
              <input
                id="rao-fid-idnum"
                value={idNumber}
                onChange={(e) =>
                  onChange(patchExtra(person, { idNumber: e.currentTarget.value }))
                }
                readOnly={readOnly}
                style={changedFieldStyle(inputStyle, ic('idNumber'))}
                title="Fiduciary ID Number"
                maxLength={30}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle} htmlFor="rao-fid-idissuer">
                ID Issuer (Field 256)
              </label>
              <input
                id="rao-fid-idissuer"
                value={idIssuer}
                onChange={(e) =>
                  onChange(patchExtra(person, { idIssuer: e.currentTarget.value }))
                }
                readOnly={readOnly}
                style={changedFieldStyle(inputStyle, ic('idIssuer'))}
                title="ID Issuer (state, country, SSA, VA, USMC)"
                maxLength={20}
                placeholder="MO / US / SSA / VA"
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-fid-idissue">
                Issue Date (Field 257)
              </label>
              <input
                id="rao-fid-idissue"
                type="date"
                value={idIssueDate}
                onChange={(e) =>
                  onChange(patchExtra(person, { idIssueDate: e.currentTarget.value }))
                }
                readOnly={readOnly}
                style={changedFieldStyle(inputStyle, ic('idIssueDate'))}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-fid-idexp">
                Expiry Date (Field 258)
              </label>
              <input
                id="rao-fid-idexp"
                type="date"
                value={idExpiryDate}
                onChange={(e) =>
                  onChange(patchExtra(person, { idExpiryDate: e.currentTarget.value }))
                }
                readOnly={readOnly}
                style={changedFieldStyle(
                  isExpired
                    ? { ...inputStyle, borderColor: 'var(--pcb-red, #b10505)' }
                    : inputStyle,
                  ic('idExpiryDate'),
                )}
              />
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}
